import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { SERVICE_AREAS } from "../data/site";

const makeIcon = (primary) =>
  L.divIcon({
    className: "aspen-marker",
    html: `<span class="aspen-dot${primary ? " aspen-dot-primary" : ""}"></span>`,
    iconSize: primary ? [22, 22] : [10, 10],
    iconAnchor: primary ? [11, 11] : [5, 5],
  });

const primaryIcon = makeIcon(true);
const dotIcon = makeIcon(false);

const FlyController = ({ active }) => {
  const map = useMap();
  useEffect(() => {
    if (active) map.flyTo(active.coords, 9, { duration: 1.4 });
  }, [active, map]);
  return null;
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { delay, duration: 1, ease: [0.16, 1, 0.3, 1] },
});

export default function ServiceMap() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="areas"
      data-testid="service-map-section"
      className="relative border-t border-white/5 bg-navy-950 px-6 py-28 md:px-10 md:py-40"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-16 grid gap-12 md:mb-20 lg:grid-cols-[1fr_1.1fr] lg:items-end">
          <div>
            <motion.p {...fadeUp(0)} className="text-[0.6rem] font-semibold uppercase tracking-[0.45em] text-gold md:text-xs">
              Service Areas
            </motion.p>
            <motion.h2
              {...fadeUp(0.1)}
              className="mt-5 font-serif text-2xl font-semibold leading-tight text-white sm:text-3xl lg:text-4xl"
              data-testid="service-map-heading"
            >
              Where We Build.
            </motion.h2>
            <motion.p {...fadeUp(0.18)} className="mt-6 max-w-md text-sm font-light leading-relaxed text-slate-400">
              From our Limebank Road workshop to the shores of the St. Lawrence —
              hover a region to travel the map.
            </motion.p>
          </div>

          <motion.div {...fadeUp(0.22)} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {SERVICE_AREAS.primary.map((area) => (
              <button
                key={area.name}
                onMouseEnter={() => setActive(area)}
                onFocus={() => setActive(area)}
                onMouseLeave={() => setActive(null)}
                data-testid={`area-card-${area.name.toLowerCase()}`}
                className="group border border-white/10 bg-navy-900/50 p-5 text-left backdrop-blur-xl transition-colors duration-500 hover:border-gold/40 hover:bg-navy-900/80"
              >
                <p className="flex items-center gap-3 font-serif text-base font-semibold text-white transition-colors duration-500 group-hover:text-gold">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                  {area.name}
                </p>
                <p className="mt-2 pl-[1.15rem] text-[0.7rem] font-light leading-relaxed tracking-wide text-slate-400">
                  {area.blurb}
                </p>
              </button>
            ))}
          </motion.div>
        </div>

        <motion.div
          {...fadeUp(0.1)}
          className="aspen-map relative border border-white/10 shadow-[0_32px_80px_rgba(0,0,0,0.5)]"
          data-lenis-prevent
        >
          <MapContainer
            bounds={[
              [44.05, -77.25],
              [45.95, -74.15],
            ]}
            zoomControl={false}
            scrollWheelZoom={false}
            attributionControl={false}
            className="h-[420px] w-full bg-navy-950 md:h-[560px]"
            data-testid="service-map-canvas"
          >
            <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Circle
              center={[45.1, -75.7]}
              radius={95000}
              pathOptions={{
                color: "#D4AF37",
                weight: 1,
                dashArray: "3 7",
                fillColor: "#D4AF37",
                fillOpacity: 0.03,
              }}
            />
            <FlyController active={active} />
            {SERVICE_AREAS.primary.map((area) => (
              <Marker key={area.name} position={area.coords} icon={primaryIcon}>
                <Tooltip permanent direction="top" offset={[0, -16]} className="aspen-tooltip">
                  {area.name}
                </Tooltip>
                <Popup className="aspen-popup">
                  <span className="aspen-popup-title">{area.name}</span>
                  <span className="aspen-popup-blurb">{area.blurb}</span>
                </Popup>
              </Marker>
            ))}
            {SERVICE_AREAS.surrounding.map((area) => (
              <Marker key={area.name} position={area.coords} icon={dotIcon}>
                <Tooltip direction="top" offset={[0, -8]} className="aspen-tooltip">
                  {area.name}
                </Tooltip>
              </Marker>
            ))}
          </MapContainer>
          <p className="pointer-events-none absolute bottom-3 right-4 z-[500] text-[0.55rem] uppercase tracking-[0.25em] text-slate-500">
            © OpenStreetMap · © CARTO
          </p>
        </motion.div>

        <motion.div {...fadeUp(0.15)} className="mt-10 flex flex-wrap gap-x-6 gap-y-3" data-testid="surrounding-areas-list">
          <span className="text-[0.6rem] font-semibold uppercase tracking-[0.35em] text-gold">
            Also Serving
          </span>
          {SERVICE_AREAS.surrounding.map((area) => (
            <span key={area.name} className="text-[0.65rem] font-light uppercase tracking-[0.25em] text-slate-500">
              {area.name}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
